// Step-by-step webhook set-up in the vendor's own dashboard: the content of the former Settings › Integrations
// tabs (xml_import/IntegrationsVentrata, -Rezdy, -Bokun, review I5 #2), now per system and shown by
// WebhookEndpoints with the account's real addresses (slug included) and token. Content per vendor only: the
// rendering is generic, and a system without an entry simply has no guide. `**x**` is shown bold.
import type { WebhookType } from '../integrationTypes';

/** A value the step needs, filled in from the API: an endpoint (by webhook type) or the token header. */
export type GuideValue = { url: WebhookType } | 'token';

export interface GuideStep {
    title: string;
    text?: string;
    list?: string[];
    after?: string;
    values?: GuideValue[];
    image?: { src: string; alt: string };
}

export interface SetupGuide {
    intro: string;
    steps: GuideStep[];
}

const IMAGES = 'https://public.curatedplanet.com/integration/setup';

export const SETUP_GUIDES: Record<string, SetupGuide> = {
    ventrata: {
        intro: 'Ventrata sends bookings through its **Booking update** webhook, set up in the Ventrata dashboard in five steps.',
        steps: [
            {
                title: 'Create a connection',
                text: 'Go to **Resellers → Connections** and click **New Connection**.',
                image: { src: `${IMAGES}/ventrata/ventrata-1.png`, alt: 'Ventrata: Resellers, Connections, New Connection' },
            },
            {
                title: 'Choose the connection type',
                text: 'Select the **OCTO API** connection type.',
                image: { src: `${IMAGES}/ventrata/ventrata-2.png`, alt: 'Ventrata: the OCTO API connection type' },
            },
            {
                title: 'Set up the connection',
                list: [
                    'Set the connection name.',
                    'Give the connection read permission.',
                    'Enable at least these capabilities: **octo/pickups**, **octo/pricing**, **octo/checkin**, **octo/resources** and **octo/gifts**. Enabling all of them is safe.',
                    'Select **dashboard** mode.',
                ],
                after: 'Then save.',
                image: { src: `${IMAGES}/ventrata/ventrata-3.png`, alt: 'Ventrata: the new connection’s properties' },
            },
            {
                title: 'Create a webhook',
                text: 'Go to **Resellers → Webhooks** and click **New Webhook**.',
                image: { src: `${IMAGES}/ventrata/ventrata-4.png`, alt: 'Ventrata: Resellers, Webhooks, New Webhook' },
            },
            {
                title: 'Configure and save the webhook',
                list: [
                    'Choose the connection you just created and set the webhook name.',
                    'Tick **Retry on error**.',
                    'Choose the **Booking Update** event.',
                    'Set the URL to the address below.',
                    'Enable at least the same capabilities as the connection.',
                ],
                after: 'Then save.',
                values: [{ url: 'booking' }, 'token'],
                image: { src: `${IMAGES}/ventrata/ventrata-5.png`, alt: 'Ventrata: the webhook’s properties' },
            },
        ],
    },
    rezdy: {
        intro: 'Rezdy sends bookings through three webhooks, **New Order**, **Updated Order** and **Cancelled Order**: set up all three, from the Rezdy dashboard.',
        steps: [
            {
                title: 'Create a webhook',
                text: 'Go to **Integrations → Webhooks** and click **New Webhooks**.',
                image: { src: `${IMAGES}/rezdy/rezdy-1.png`, alt: 'Rezdy: Integrations, Webhooks, New Webhooks' },
            },
            {
                title: 'Set up the webhook',
                list: [
                    'Enter a webhook name.',
                    'Choose one type: **New Order**, **Updated Order** or **Cancelled Order**.',
                    'Enter the address below as the **Target URL**.',
                ],
                after: 'Then click **Create**, and repeat for the other two types.',
                values: [{ url: 'booking' }, 'token'],
                image: { src: `${IMAGES}/rezdy/rezdy-2.png`, alt: 'Rezdy: the new webhook’s properties' },
            },
        ],
    },
    bokun: {
        intro: 'Bókun sends bookings through an **HTTP Booking notification**, set up in the Bókun dashboard in three steps.',
        steps: [
            {
                title: 'Open Integration Configurations',
                text: 'Choose **Settings** in the menu, scroll down to **Connections** and choose **Integration Configurations**.',
                image: { src: `${IMAGES}/bokun/bokun-1.png`, alt: 'Bókun: Settings, Connections, Integration Configurations' },
            },
            {
                title: 'Choose the system type',
                text: 'Click **Add** in the upper right corner, then choose the **HTTP Booking notification** system type.',
                image: { src: `${IMAGES}/bokun/bokun-3.png`, alt: 'Bókun: the HTTP Booking notification system type' },
            },
            {
                title: 'Configure the integration',
                list: [
                    'Enter a title and make sure the integration is active.',
                    'Enter the address below as the **Target URL**.',
                    'Make sure the data format is **json**.',
                    'Tick all three: **Notify on booking confirmed**, **Notify on booking updated** and **Notify on booking cancelled**.',
                ],
                after: 'Then click **Save**.',
                values: [{ url: 'booking' }, 'token'],
                image: { src: `${IMAGES}/bokun/bokun-4.png`, alt: 'Bókun: the integration’s properties' },
            },
        ],
    },
};

/** The guide of a system whose webhooks the client sets up itself: `manual`, or `api` where [Install webhooks] is
 *  not offered (a sandbox). Production Ventrata shows Install, not the guide; support-made set-ups have none. */
export const guideFor = (system: string, setupMode: string, installAvailable: boolean): SetupGuide | null => {
    const byHand = setupMode === 'manual' || (setupMode === 'api' && !installAvailable);
    return byHand ? SETUP_GUIDES[system] || null : null;
};
